import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, devicesAPI } from '../services/api';
import { Server, Grid3X3, FileText, Wifi, RefreshCw } from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [s, d, l] = await Promise.all([
        adminAPI.dashboard(),
        devicesAPI.list(),
        adminAPI.logs(8),
      ]);
      setStats(s.data);
      setDevices(d.data);
      setLogs(l.data);
    } catch (e) { console.error(e); }
    setLastUpdate(new Date());
    setLoading(false);
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      // Auto-sync device status + box status every 2 minutes
      devicesAPI.checkAllStatus()
        .then(() => load())
        .catch(() => {});
    }, 120000);
    return () => clearInterval(interval);
  }, []);

  const [lastUpdate, setLastUpdate] = React.useState(null);

  const statCards = stats ? [
    { label: 'Total Devices', value: stats.total_devices, icon: Server, color: '#4d90fe', bg: 'rgba(77,144,254,0.1)' },
    { label: 'Total Boxes', value: stats.total_boxes, sub: `${stats.available_boxes} available · ${stats.occupied_boxes} occupied`, icon: Grid3X3, color: '#8BC63E', bg: 'rgba(139,198,62,0.1)' },
    { label: 'Active Parcels', value: stats.active_orders, sub: 'Stored in lockers', icon: FileText, color: '#ffb020', bg: 'rgba(255,176,32,0.1)' },
    { label: 'Online Devices', value: `${stats.online_devices}/${stats.total_devices}`, icon: Wifi, color: stats.online_devices === stats.total_devices ? '#8BC63E' : '#ff4466', bg: stats.online_devices === stats.total_devices ? 'rgba(139,198,62,0.1)' : 'rgba(255,68,102,0.1)' },
  ] : [];

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastUpdate && <span style={{ fontSize: 11, color: '#8a8da4' }}>Updated {lastUpdate.toLocaleTimeString()}</span>}
          <button style={s.refreshBtn} onClick={load}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : (
        <>
          <div style={s.statsGrid}>
            {statCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} style={s.statCard}>
                  <div style={s.statTop}>
                    <span style={s.statLabel}>{c.label}</span>
                    <div style={{ ...s.statIconWrap, background: c.bg }}><Icon size={16} color={c.color} /></div>
                  </div>
                  <div style={{ ...s.statValue, color: c.color }}>{c.value}</div>
                  {c.sub && <div style={s.statSub}>{c.sub}</div>}
                </div>
              );
            })}
          </div>

          <div style={s.grid2}>
            <div style={s.panel}>
              <div style={s.panelHead}>
                <span style={s.panelTitle}>Devices</span>
                <span style={s.panelAction} onClick={() => navigate('/devices')}>View All →</span>
              </div>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Device</th>
                    <th style={s.th}>Location</th>
                    <th style={s.th}>Boxes</th>
                    <th style={s.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <tr key={d.id} style={s.tr}>
                      <td style={s.td}>
                        <div style={s.deviceNum}>{d.device_number}</div>
                        <div style={s.deviceName}>{d.name}</div>
                      </td>
                      <td style={s.td}>{d.location || '—'}</td>
                      <td style={s.td}>{d.box_count}</td>
                      <td style={s.td}>
                        <span style={d.is_online ? s.badgeGreen : s.badgeRed}>
                          <span style={{ ...s.dot, background: d.is_online ? '#8BC63E' : '#ff4466' }} />
                          {d.is_online ? 'Online' : 'Offline'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={s.panel}>
              <div style={s.panelHead}>
                <span style={s.panelTitle}>Recent Activity</span>
                <span style={s.panelAction} onClick={() => navigate('/logs')}>View All →</span>
              </div>
              <div>
                {logs.length === 0 ? (
                  <div style={s.empty}>No activity yet</div>
                ) : (
                  logs.map((l) => (
                    <div key={l.id} style={s.logEntry}>
                      <div style={s.logTime}>{(l.created_at || '').replace('T', ' ').slice(5, 16)}</div>
                      <div style={s.logMsg}>
                        <strong style={{ color: '#8BC63E' }}>{l.action.replace(/_/g, ' ')}</strong>
                        {l.details && <span> — {l.details}</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#e4e5ed' },
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
    background: 'transparent', border: '1px solid #252733', borderRadius: 8,
    color: '#8789a0', fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
  loading: { textAlign: 'center', padding: 60, color: '#8a8da4', fontSize: 14 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  statCard: {
    background: '#0f1117', border: '1px solid #252733', borderRadius: 12, padding: 20,
  },
  statTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  statLabel: { fontSize: 12, color: '#8789a0', fontWeight: 500 },
  statIconWrap: { width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statValue: { fontSize: 28, fontWeight: 700, fontFamily: "'Space Mono', monospace", letterSpacing: -1 },
  statSub: { fontSize: 11, color: '#8a8da4', marginTop: 6 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  panel: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, overflow: 'hidden' },
  panelHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', borderBottom: '1px solid #252733',
  },
  panelTitle: { fontSize: 14, fontWeight: 600, color: '#e4e5ed' },
  panelAction: { fontSize: 12, color: '#8BC63E', cursor: 'pointer', fontWeight: 500 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '10px 16px', fontSize: 10, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.8, color: '#8a8da4',
    background: '#0a0b0e', borderBottom: '1px solid #252733',
  },
  tr: {},
  td: { padding: '12px 16px', fontSize: 13, borderBottom: '1px solid #1a1b25', color: '#e4e5ed' },
  deviceNum: { fontFamily: "'Space Mono', monospace", fontSize: 12 },
  deviceName: { fontSize: 11, color: '#8789a0', marginTop: 2 },
  badgeGreen: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
    borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: 'rgba(139,198,62,0.1)', color: '#8BC63E',
  },
  badgeRed: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
    borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: 'rgba(255,68,102,0.1)', color: '#ff4466',
  },
  dot: { width: 6, height: 6, borderRadius: '50%' },
  logEntry: {
    display: 'flex', gap: 12, padding: '11px 20px', borderBottom: '1px solid #1a1b25',
  },
  logTime: { fontFamily: "'Space Mono', monospace", fontSize: 11, color: '#8a8da4', whiteSpace: 'nowrap', paddingTop: 1 },
  logMsg: { fontSize: 13, color: '#8789a0', flex: 1 },
  empty: { padding: 40, textAlign: 'center', color: '#8a8da4', fontSize: 13 },
};

